import { useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { useCallback } from 'react';

import { type GetBoardStatsResponse } from '@air/api';
import { type Board } from '@air/api/types';

import { GalleryItemType } from '~/components/Gallery/types';
import { isPrivateBoardStatsKey } from '~/components/v2/hooks/queries/usePrivateBoardsStats';

export const useUpdateBoardStats = () => {
  const queryClient = useQueryClient();
  const updateBoardStats = useCallback(
    (
      boardId: Board['id'],
      updateFn?: (data?: GetBoardStatsResponse) => GetBoardStatsResponse | undefined,
      revalidate = true,
    ) => {
      const boardStatsQueries = queryClient.getQueriesData({
        predicate: ({ queryKey }) => {
          return isPrivateBoardStatsKey(queryKey) && queryKey[1].boardId === boardId;
        },
      });

      boardStatsQueries.forEach(([queryKey]) => {
        queryClient.setQueryData<GetBoardStatsResponse | undefined>(queryKey, updateFn);
      });

      if (revalidate) {
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) => {
            return isPrivateBoardStatsKey(queryKey) && queryKey[1].boardId === boardId;
          },
        });
      }
    },
    [queryClient],
  );

  return {
    updateBoardStats,
  };
};

export const useUpdateItemsCount = () => {
  const { updateBoardStats } = useUpdateBoardStats();

  const removeItems = useCallback(
    (boardId: Board['id'], count: number, type: GalleryItemType.asset | GalleryItemType.file) => {
      updateBoardStats(
        boardId,
        (data) =>
          data &&
          produce(data, (draft) => {
            if (type === GalleryItemType.file) {
              draft.fileCount = Math.max(0, draft.fileCount - count);
            } else {
              draft.assetCount = Math.max(0, draft.assetCount - count);
            }
          }),
      );
    },
    [updateBoardStats],
  );

  return {
    removeItems,
  };
};
